import React, { FC, useState } from 'react';

import { NativeResources } from '@ariksa/compliance-policies/api';
import { ResourceMapping } from '@ariksa/inventory-core/api';
import {
  Box,
  Center,
  Flex,
  HStack,
  ListItem,
  Stack,
  Text,
  Textarea,
  UnorderedList,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import {
  ConfirmationInput,
  DetailsIdentifier,
  renderExceptionExpiresOn,
  StackedHeader,
} from 'components/DataDisplay';
import {
  CancelButton,
  PrimaryButton,
  Select,
  textAreaFieldStyles,
} from 'components/DataEntry';
import { ChatBubbleIcon, IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { renderDetailsFieldWithIcon } from 'components/Overlay/Modal/AddExceptionModal/utils';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useAlerts } from 'containers/Findings/Alerts/Components/AlertsTable/hooks/useAlerts';
import { exceptionApplyTimeOptions } from 'containers/Findings/Alerts/Components/utils';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  resourceType: ResourceMapping;
  resourceIds: string[];
  blueprintIds: string[];
  noOfAccounts: number;
}

export const ExcludeResourcesConfirmationModal: FC<Props> = props => {
  const {
    isOpen,
    onClose,
    onSuccess,
    resourceType,
    resourceIds,
    blueprintIds,
    noOfAccounts,
  } = props;
  const { environmentId } = useAccessBoundary();
  const { getAggregatedFindings } = useAlerts();
  const [reason, setReason] = useState<string>('');
  const [command, setCommand] = useState<string>('');
  const dispatch = useDispatch();
  const [exceptionApplyTime, setExceptionApplyTime] = useState({
    label: '6 months',
    value: dayjs().utc().add(6, 'months').format(),
  });
  const { addException } = useSelector(selectSharedState);

  const addResourceException = () => {
    dispatch(
      sharedStateActions.addException({
        q: {
          blueprintExceptionCreate: {
            resource_ids: resourceIds,
            native_resource: resourceType?.cloud_native_class as NativeResources,
            blueprint_ids: blueprintIds,
            environment_id: environmentId!,
            description: reason,
            expire_at: exceptionApplyTime?.value,
          },
        },
        onSuccess: () => {
          onClose();
          onSuccess();
          getAggregatedFindings();
        },
      }),
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          upper="Apply Exception To All Policies for Resource"
          lower={
            resourceType?.cloud_native_name +
            ' / ' +
            resourceType?.cloud_agnostic_name
          }
          type={IconTypes.Exception}
        />
      }
      body={
        <Stack h="full" pt={3} spacing={4}>
          <DetailsIdentifier
            label="No of Resources"
            value={resourceIds.length}
          />
          <DetailsIdentifier label="No of Accounts" value={noOfAccounts} />
          <Box pt={2}>
            <DetailsIdentifier
              label={renderDetailsFieldWithIcon(
                IconTypes.Clock,
                'Duration For Which Exception Applies',
                customTheme.colors.orange['300'],
              )}
              showColon={false}
              value={
                <Stack spacing={1} pt={1}>
                  <Box w={'50%'}>
                    <Select
                      value={exceptionApplyTime}
                      options={exceptionApplyTimeOptions}
                      onChange={opt => {
                        setExceptionApplyTime?.(opt);
                      }}
                      menuPortalTarget={document.body}
                      showClearIndicator={false}
                    />
                  </Box>
                  <Box fontSize="sm">
                    {renderExceptionExpiresOn(exceptionApplyTime?.value)}
                  </Box>
                </Stack>
              }
              isInline={false}
            />
          </Box>
          <DetailsIdentifier
            label={
              <HStack pt={3}>
                <Box boxSize={5}>
                  <Center color="primary">
                    <ChatBubbleIcon />
                  </Center>
                </Box>
                <Flex fontWeight={600} whiteSpace="nowrap">
                  Reason for exclusion{' '}
                  <Text color={'red'} pl={1}>
                    *
                  </Text>
                </Flex>
              </HStack>
            }
            styles={{ value: { pt: 1 } }}
            showColon={false}
            isInline={false}
            spacing={1}
            value={
              <Box w="full" pr={7}>
                <Textarea
                  {...textAreaFieldStyles.textArea}
                  placeholder="Provide a reason for the exclusion"
                  onChange={e => {
                    setReason(e.target.value.trim());
                  }}
                />
              </Box>
            }
          />
          <Stack fontSize="sm">
            <Box color="primary">NOTE:</Box>
            <UnorderedList spacing={2}>
              <ListItem pl={3}>
                Applying this exception will automatically resolve all current
                violations with “Exception” as the reason
              </ListItem>
              <ListItem pl={3}>
                When the exception expires, this environment will be
                automatically assessed for violations associated with this
                policy
              </ListItem>
            </UnorderedList>
          </Stack>
          <ConfirmationInput
            confirmationText="CONFIRM"
            label={
              <Text>
                Type <Text as="b">“CONFIRM”</Text> below to exclude all these
                resources from all applicable policies.
              </Text>
            }
            command={command}
            setCommand={setCommand}
            inputWidth="full"
          />
        </Stack>
      }
      footer={
        <HStack w="full" justify="space-between">
          <CancelButton onClick={() => onClose()}>CANCEL</CancelButton>
          <PrimaryButton
            px={7}
            isDisabled={
              !reason || command !== 'CONFIRM' || addException.isLoading
            }
            onClick={addResourceException}
            isLoading={addException.isLoading}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
      styles={{ content: { maxW: '900px' } }}
    />
  );
};
